import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
  adminOnly?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireSubscription = true,
  adminOnly = false,
}) => {
  const { user, loading, subscription, retrySubscriptionCheck } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-6 max-w-sm mx-auto px-6">
          <Skeleton className="h-16 w-16 rounded-2xl mx-auto" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-32 mx-auto" />
            <Skeleton className="h-3 w-24 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (subscription.status === 'idle' || subscription.status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-6 max-w-sm mx-auto px-6 animate-fade-in">
          <div className="relative">
            <Skeleton className="h-20 w-20 rounded-2xl mx-auto" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full bg-primary/20 animate-pulse" />
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-foreground font-display text-lg">Confirmando seu acesso...</p>
            <p className="text-sm text-muted-foreground font-body">
              Isso leva apenas um momento
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (subscription.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-6 space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <div>
            <h2 className="text-xl font-display font-bold text-foreground mb-2">
              Ops! Algo deu errado
            </h2>
            <p className="text-muted-foreground font-body">
              {subscription.error || 'Não foi possível verificar sua assinatura.'}
            </p>
          </div>
          <Button
            onClick={retrySubscriptionCheck}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  // Admin bypass
  if (subscription.isAdmin) {
    if (location.pathname === '/payment') {
      return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
  }

  if (adminOnly) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireSubscription && !subscription.subscribed) {
    return <Navigate to="/payment" replace />;
  }

  return <>{children}</>;
};
